/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import AddEditProfileAction from "./AddEditProfileAction";
import AddEditProfileRule from "./AddEditProfileRule";
import AddEditProfileSchedule from "./AddEditProfileSchedule";
import { constStr } from "../../../../globals/constants";
import ConfirmDialog from "../../../Shared/Dialogs/ConfirmDialog";
import { X, Check } from "lucide-react";
import { setBasicDialogOpenFor, setConfirmDialog, setOpenToast } from "../../../../store/helpers/helperReducers";
import { DIALOGOPENFOR } from "../../../../globals/globals";
import { basicDialogSelector, confirmDialogSelector } from "../../../../store/helpers/helperSelectors";
import { useSelector } from "react-redux";
import { useStoreDispatch } from "../../../../store/hooks";
import { IThresholdCrossingSettingProfile, IThresholdCrossingSettingAction, IThresholdCrossingSettingRule, IThresholdCrossingSettingSchedule } from "../../../../store/api/admin/types";
import { resetThresholdCrossingSettingRule, resetThresholdCrossingSettingAction, resetThresholdCrossingSettingSchedule } from "../../../../components/helpers";
import { useThresholdCrossingSetting, useThresholdCrossingSettingProfileData } from "./hooks";
import { setThresholdCrossingSettingProfileModelOpenMode, setThresholdCrossingSettingActionModelOpenMode, setThresholdCrossingSettingScheduleModelOpenMode, setThresholdCrossingSettingRuleModelOpenMode, setSelectedThresholdCrossingSettingSchedule, setIsAddEditDeleteThresholdCrossingSettingAction, setIsAddEditDeleteThresholdCrossingSettingSchedule, setIsAddEditDeleteThresholdCrossingSettingRule } from "../../../../store/api/admin/reducers";
import { thresholdCrossingSettingActionModelOpenModeSelector, thresholdCrossingSettingScheduleModelOpenModeSelector, thresholdCrossingSettingRuleModelOpenModeSelector } from "../../../../store/api/admin/selectors";
import { selectedThresholdCrossingSettingActionSelector, selectedThresholdCrossingSettingScheduleSelector, selectedThresholdCrossingSettingRuleSelector, isAddEditDeleteThresholdCrossingSettingActionSelector, isAddEditDeleteThresholdCrossingSettingScheduleSelector, isAddEditDeleteThresholdCrossingSettingRuleSelector } from "../../../../store/api/admin/selectors";
import { useGetThresholdCrossingSettingActionQuery, useGetThresholdCrossingSettingScheduleQuery, useGetThresholdCrossingSettingRuleQuery } from "../../../../store/api/admin/adminApiSlice";
import { Button } from "../../../ui/button";
import { Label } from "../../../ui/label";
import { Input } from "../../../ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../ui/tabs";
import { Icon } from "../../../Shared/Icon";
import { Switch } from "../../../ui/switch";
import DeleteIcon from '@mui/icons-material/Delete';
import ModeEditOutlineIcon from '@mui/icons-material/ModeEditOutline';
import AddIcon from '@mui/icons-material/Add';
import CustomDataGrid from "../../../Shared/Table/CustomDataGrid";
import { thresholdCrossingSettingActionColumns, thresholdCrossingSettingRuleColumns } from "../../../Shared/Table/Columns";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

interface IAddEditThresholdCrossingSettingProps {
  selectedProfileRow: IThresholdCrossingSettingProfile;
  onDeleteProfile?: () => void;
  canManage: boolean;
}

const ThresholdCrossingSettings: React.FC<IAddEditThresholdCrossingSettingProps> = ({ selectedProfileRow, onDeleteProfile, canManage }) => {

  const {
    profileList,
    handleOnActionRowSelected,
    handleOnRuleRowSelected,
    deleteThresholdCrossingSettingActionClickAction,
    deleteThresholdCrossingSettingRuleClickAction,
    deleteThresholdCrossingSettingScheduleClickAction,
    deleteSchedule
  } = useThresholdCrossingSetting();

  const [activeTab, setActiveTab] = useState("0");

  const dispatch = useStoreDispatch();
  const { data = [], isLoading, refetch } = useGetThresholdCrossingSettingRuleQuery({ id: selectedProfileRow.id }, { skip: selectedProfileRow.id === 0 });
  const [rowsRuleData, setRowsRuleData] = useState<IThresholdCrossingSettingRule[]>([]);

  const basicDialogOpenFor = useSelector(basicDialogSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const isAddEditDeleteThresholdCrossingSettingRule = useSelector(isAddEditDeleteThresholdCrossingSettingRuleSelector);
  const isAddEditDeleteThresholdCrossingSettingSchedule = useSelector(isAddEditDeleteThresholdCrossingSettingScheduleSelector);
  const isAddEditDeleteThresholdCrossingSettingAction = useSelector(isAddEditDeleteThresholdCrossingSettingActionSelector);
  const [selectedRuleRowIDs, setSelectedRuleRowIDs] = useState<any[]>([]);
  const [selectedActionRowIDs, setSelectedActionRowIDs] = useState<any[]>([]);

  const { data: actionList = [], isLoading: actionIsLoading, refetch: actionRefetch } = useGetThresholdCrossingSettingActionQuery({ id: selectedProfileRow.id }, { skip: selectedProfileRow.id === 0 });
  const [rowsActionData, setRowsActionData] = useState<IThresholdCrossingSettingAction[]>([]);

  const { data: ScheduleList = [], isLoading: scheduleIsLoading, refetch: scheduleRefetch } = useGetThresholdCrossingSettingScheduleQuery({ id: selectedProfileRow.id }, { skip: selectedProfileRow.id === 0 });
  const [rowsScheduleData, setRowsScheduleData] = useState<IThresholdCrossingSettingSchedule[]>([]);

  const thresholdCrossingSettingActionModelOpenMode = useSelector(thresholdCrossingSettingActionModelOpenModeSelector);
  const thresholdCrossingSettingScheduleModelOpenMode = useSelector(thresholdCrossingSettingScheduleModelOpenModeSelector);
  const thresholdCrossingSettingRuleModelOpenMode = useSelector(thresholdCrossingSettingRuleModelOpenModeSelector);

  const selectedThresholdCrossingSettingAction = useSelector(selectedThresholdCrossingSettingActionSelector);
  const selectedThresholdCrossingSettingSchedule = useSelector(selectedThresholdCrossingSettingScheduleSelector);
  const selectedThresholdCrossingSettingRule = useSelector(selectedThresholdCrossingSettingRuleSelector);

  const { submitHandler, thresholdCrossingSettingProfileData, setThresholdCrossingSettingProfileData, name, description, enabled } = useThresholdCrossingSettingProfileData(selectedProfileRow, onDeleteProfile);

  useEffect(() => {
    const LoadRuleData = async () => {
      if (isAddEditDeleteThresholdCrossingSettingRule) {
        const { data: newData = [] } = await refetch();
        setRowsRuleData(newData);
        dispatch(setIsAddEditDeleteThresholdCrossingSettingRule(false));
      }
    };
    LoadRuleData();
  }, [
    isAddEditDeleteThresholdCrossingSettingRule,
    refetch
  ]);

  useEffect(() => {
    setRowsRuleData(selectedProfileRow.rules);
  }, [selectedProfileRow.rules]);

  useEffect(() => {
    if (selectedActionRowIDs.length > 0) {
      setSelectedActionRowIDs([]);
    }
  }, [actionList]);

  useEffect(() => {
    if (selectedRuleRowIDs.length > 0) {
      setSelectedRuleRowIDs([]);
    }
  }, [data]);

  useEffect(() => {
    const LoadScheduleData = async () => {
      if (isAddEditDeleteThresholdCrossingSettingSchedule) {
        const { data: newData = [] } = await scheduleRefetch();
        setRowsScheduleData(ScheduleDataIn12HFormat(newData));
        dispatch(setIsAddEditDeleteThresholdCrossingSettingSchedule(false));
      }
    };
    LoadScheduleData();
  }, [
    isAddEditDeleteThresholdCrossingSettingSchedule,
    scheduleRefetch
  ]);

  useEffect(() => {
    setRowsScheduleData(ScheduleDataIn12HFormat(selectedProfileRow.schedules));
  }, [selectedProfileRow.schedules]);

  useEffect(() => {
    const LoadActionData = async () => {
      if (isAddEditDeleteThresholdCrossingSettingAction) {
        const { data: actionData = [] } = await actionRefetch();
        setRowsActionData(actionData);
        dispatch(setIsAddEditDeleteThresholdCrossingSettingAction(false));
      }
    };
    LoadActionData();
  }, [
    isAddEditDeleteThresholdCrossingSettingAction,
    actionRefetch
  ]);

  useEffect(() => {
    setRowsActionData(selectedProfileRow.actions);
  }, [selectedProfileRow.actions]);

  const ScheduleDataIn12HFormat = (scheduleList: IThresholdCrossingSettingSchedule[]) => {
    return scheduleList.map(item => ({
      ...item,
      toTime: convertTo12Hour(item.toTime),
      fromTime: convertTo12Hour(item.fromTime)
    }));
  };

  function convertTo12Hour(time24: any) {
    let [hours, minutes] = time24.split(":");
    hours = parseInt(hours, 10);

    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${hours.toString().padStart(2, "0")}:${minutes} ${ampm}`;
  }

  const handleScheduleChange = (newValue: string) => {
    setActiveTab(newValue);
  };

  const handleEditProfile = () => {
    dispatch(setThresholdCrossingSettingProfileModelOpenMode("Edit"));
    dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.THRESHOLDCROSSINGSETTINGPROFILE));
  };

  const handleDeleteProfile = () => {
    dispatch(setConfirmDialog(DIALOGOPENFOR.DELETETHRESHOLDCROSSINGSETTINGPROFILE));
  };

  const handleDeleteProfileConfirm = (actionName: string) => {
    if (actionName.toUpperCase() === constStr.yesDelete.toUpperCase()) {
      onDeleteProfile?.();
    }
  };

  const handleDeleteSchedule = (schedule: IThresholdCrossingSettingSchedule) => {
    if (rowsScheduleData.length <= 1) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "warning",
          message: "You can not delete this schedule. At least one schedule is required.",
        })
      );
    }
    else {
      dispatch(setSelectedThresholdCrossingSettingSchedule(schedule));
      dispatch(setConfirmDialog(DIALOGOPENFOR.DELETETHRESHOLDCROSSINGSETTINGSCHEDULE));
    }
  };

  const handleDeleteScheduleConfirm = (actionName: string) => {
    if (actionName.toUpperCase() === constStr.yesDelete.toUpperCase()) {
      deleteSchedule(selectedThresholdCrossingSettingSchedule);
    }
  };

  const handleAddAction = () => {
    if (rowsActionData.length >= 1) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: "A profile cannot have more than 1 action.",
        })
      );
      return;
    }

    dispatch(setThresholdCrossingSettingActionModelOpenMode("Add"));
    dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.THRESHOLDCROSSINGSETTINGACTION));
  };

  const handleAddSchedule = () => {
    if (rowsScheduleData.length >= 3) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: "A profile cannot have more than 3 schedules.",
        })
      );
      return;
    }
    dispatch(setThresholdCrossingSettingScheduleModelOpenMode("Add"));
    dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.THRESHOLDCROSSINGSETTINGSCHEDULE));
  };

  const handleEditSchedule = (selectedItem: any) => {
    dispatch(setThresholdCrossingSettingScheduleModelOpenMode("Edit"));
    dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.THRESHOLDCROSSINGSETTINGSCHEDULE));
    dispatch(setSelectedThresholdCrossingSettingSchedule(selectedItem));
  };

  const handleAddRule = () => {
    if (rowsRuleData.length >= 10) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: "A profile cannot have more than 10 rules.",
        })
      );
      return;
    }
    dispatch(setThresholdCrossingSettingRuleModelOpenMode("Add"));
    dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.THRESHOLDCROSSINGSETTINGRULE));
  };

  useEffect(() => {
    setActiveTab((prev) => {
      const idx = parseInt(prev, 10);
      return idx >= rowsScheduleData.length ? "0" : prev;
    });
  }, [rowsScheduleData]);


  function classifyDays(days: string[]) {
    const weekdays = ["MON", "TUE", "WED", "THU", "FRI"];
    const weekends = ["SAT", "SUN"];

    // Check if all 7 days are present
    if (days.length === 7 && weekdays.every(d => days.includes(d)) && weekends.every(d => days.includes(d))) {
      return "Every day";
    }

    // Check if all days are weekdays only
    if (days.every(d => weekdays.includes(d))) {
      return "Weekdays";
    }

    // Check if all days are weekends only
    if (days.every(d => weekends.includes(d))) {
      return "Weekends";
    }

    // Otherwise mixed or partial days
    return "";
  }

  function classifyTimes(record: { fromTime: string; toTime: string }) {
    if (record.fromTime === "12:00 AM" && record.toTime === "11:59 PM") {
      return "All day";
    }
    return `${record.fromTime} – ${record.toTime}`;
  }
  const [perfNameEditing, setPerfNameEditing] = useState(false);
  const [perfNameData, setPerfNameData] = useState(selectedProfileRow?.name || "");
  const [perfNameError, setPerfNameError] = useState("");

  const [perfDescriptionEditing, setPerfDescriptionEditing] = useState(false);
  const [perfDescriptionData, setPerfDescriptionData] = useState(selectedProfileRow?.description || "");
  const [perfDescriptionError, setPerfDescriptionError] = useState("");

  const handleEditProfileNameStart = () => {
    // Cancel description editing if open
    if (perfDescriptionEditing) {
      setPerfDescriptionData(selectedProfileRow?.description || "");
      setPerfDescriptionError("");
      setPerfDescriptionEditing(false);
    }
    setPerfNameData(selectedProfileRow?.name || "");
    setPerfNameError("");
    setPerfNameEditing(true);
  };

  const handleSaveProfileName = async () => {
    if (!perfNameData.trim()) {
      setPerfNameError("Name is required");
      return;
    }

    try {
      await submitHandler();
      setPerfNameEditing(false);
    } catch (error) {
      setPerfNameError("Failed to save name in profile");
    }
  };

  const handleCancelProfileNameEdit = () => {
    setPerfNameData(selectedProfileRow?.name || "");
    setPerfNameError("");
    setPerfNameEditing(false);
  };

  const handleEditProfileDescriptionStart = () => {
    // Cancel name editing if open
    if (perfNameEditing) {
      setPerfNameData(selectedProfileRow?.name || "");
      setPerfNameError("");
      setPerfNameEditing(false);
    }
    setPerfDescriptionData(selectedProfileRow?.description || "");
    setPerfDescriptionError("");
    setPerfDescriptionEditing(true);
  };

  const handleSaveProfileDescription = async () => {
    try {
      await submitHandler();
      setPerfDescriptionEditing(false);
    } catch (error) {
      setPerfDescriptionError("Failed to save description in profile");
    }
  };

  const handleCancelProfileDescriptionEdit = () => {
    setPerfDescriptionData(selectedProfileRow?.description || "");
    setPerfDescriptionError("");
    setPerfDescriptionEditing(false);
  };

  const [pendingEnabledStatus, setPendingEnabledStatus] = useState<boolean | null>(null);
  const pendingSubmitRef = useRef(false);

  // Sync local editing state to hook state so submitHandler has current data
  useEffect(() => {
    const updatePayload = {
      ...selectedProfileRow,
      name: perfNameData,
      description: perfDescriptionData,
      enabled: pendingEnabledStatus !== null ? pendingEnabledStatus : selectedProfileRow?.enabled
    };
    setThresholdCrossingSettingProfileData(updatePayload);
  }, [thresholdCrossingSettingProfileData, perfNameData, perfDescriptionData, pendingEnabledStatus, selectedProfileRow?.id, selectedProfileRow?.enabled]);

  // Submit after thresholdCrossingSettingProfileData is updated with new enabled value
  useEffect(() => {
    if (pendingSubmitRef.current && pendingEnabledStatus !== null) {
      pendingSubmitRef.current = false;
      const submitEnabled = async () => {
        try {
          await submitHandler();
        } catch (error) {
          console.error("Error updating profile enabled status:", error);
        } finally {
          setPendingEnabledStatus(null);
        }
      };
      submitEnabled();
    }
  }, [thresholdCrossingSettingProfileData]);

  const handleEnabledToggle = () => {
    dispatch(setConfirmDialog(DIALOGOPENFOR.ENABLETHRESHOLDCROSSINGSETTINGPROFILE));
  };

  const handleEnabledConfirm = (actionName: string) => {
    if (actionName.toUpperCase() === constStr.yesSave.toUpperCase()) {
      if (selectedProfileRow?.devices?.length === 0 && !selectedProfileRow?.enabled) {
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "error",
            message: "You can not enable this profile. This profile is not currently mapped to any device.",
          })
        );
      }
      else {
        pendingSubmitRef.current = true;
        setPendingEnabledStatus(!selectedProfileRow?.enabled);
      }
    }
  };

  // Sync profile name and description data when selectedProfileRow changes
  useEffect(() => {
    if (!perfNameEditing) {
      setPerfNameData(selectedProfileRow?.name || "");
      setPerfNameError("");
    }
    if (!perfDescriptionEditing) {
      setPerfDescriptionData(selectedProfileRow?.description || "");
      setPerfDescriptionError("");
    }
  }, [selectedProfileRow?.id, selectedProfileRow?.name, selectedProfileRow?.description, perfNameEditing, perfDescriptionEditing]);

  return (
    <>
      <div
        className="flex flex-col w-full px-0 md:px-0 pt-0 pb-0"
        style={{ color: "var(--foreground)" }}
      >
        {profileList.length > 0 ? (
          <div className="flex flex-col w-full" style={{ color: "var(--foreground)" }}>
            {/* Dialogs */}
            {openConfirmDialog === DIALOGOPENFOR.DELETETHRESHOLDCROSSINGSETTINGPROFILE && (
              <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={handleDeleteProfileConfirm}>
                Are you sure you want to delete profile <strong>{selectedProfileRow?.name || ""}</strong>?
              </ConfirmDialog>
            )}
            {openConfirmDialog === DIALOGOPENFOR.ENABLETHRESHOLDCROSSINGSETTINGPROFILE && (
              <ConfirmDialog actionName={constStr.yesSave} handleClickAction={handleEnabledConfirm}>
                Are you sure you want to {selectedProfileRow?.enabled ? "disable" : "enable"} this profile?
              </ConfirmDialog>
            )}
            {basicDialogOpenFor === DIALOGOPENFOR.THRESHOLDCROSSINGSETTINGACTION && (
              <AddEditProfileAction
                modelOpenMode={thresholdCrossingSettingActionModelOpenMode}
                selectedRow={thresholdCrossingSettingActionModelOpenMode === "Add" ? { ...resetThresholdCrossingSettingAction, performanceProfileId: selectedProfileRow?.id } : selectedThresholdCrossingSettingAction}
              />
            )}
            {basicDialogOpenFor === DIALOGOPENFOR.THRESHOLDCROSSINGSETTINGSCHEDULE && (
              <AddEditProfileSchedule
                modelOpenMode={thresholdCrossingSettingScheduleModelOpenMode}
                selectedRow={thresholdCrossingSettingScheduleModelOpenMode == "Add" ? { ...resetThresholdCrossingSettingSchedule, performanceProfileId: selectedProfileRow.id } : selectedThresholdCrossingSettingSchedule}
                scheduleList={rowsScheduleData}
              />
            )}
            {basicDialogOpenFor === DIALOGOPENFOR.THRESHOLDCROSSINGSETTINGRULE && (
              <AddEditProfileRule
                modelOpenMode={thresholdCrossingSettingRuleModelOpenMode}
                selectedRow={thresholdCrossingSettingRuleModelOpenMode === "Add" ? { ...resetThresholdCrossingSettingRule, performanceProfileId: selectedProfileRow.id } : selectedThresholdCrossingSettingRule}
              />
            )}
            <div
              style={{
                paddingLeft: "5px",
                paddingRight: "5px",
                paddingBottom: "24px"
              }}
            >
              <form autoComplete="off">
                <div className="space-y-5">
                  {/* Profile Name + Actions */}
                  <div className="flex items-center justify-between gap-1">
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">Name</h4>
                      {perfNameEditing ? (
                        <div className="flex flex-col gap-2 mt-2">
                          <div className="flex items-center gap-2">
                            <div className="w-full space-y-2">
                              <Input
                                id="profile-name-edit"
                                name="name"
                                value={perfNameData}
                                onChange={(e) => {
                                  setPerfNameData(e.target.value);
                                  setPerfNameError("");
                                }}
                                onBlur={() => {
                                  if (!perfNameData.trim()) {
                                    setPerfNameError("Name is required");
                                  }
                                }}
                                maxLength={255}
                                tooltipText="Maximum length: 255 characters"
                                className="w-full"
                                autoFocus
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <X onClick={handleCancelProfileNameEdit} className="h-4 w-4 cursor-pointer text-destructive" />
                              <Check
                                onClick={perfNameData.trim() && perfNameData !== selectedProfileRow?.name ? handleSaveProfileName : undefined}
                                className={`h-4 w-4 ${perfNameData.trim() && perfNameData !== selectedProfileRow?.name ? "cursor-pointer text-green-500" : "text-muted-foreground opacity-50"}`}
                              />
                            </div>
                          </div>
                          {perfNameError && <p className="text-sm mt-1" style={{ color: "var(--destructive)" }}>{perfNameError}</p>}
                        </div>
                      ) : (
                        <div
                          className="group/name relative inline-flex items-center gap-1.5 rounded-md px-2 py-1 -mx-2 -my-1 cursor-pointer transition-colors hover:bg-secondary hover:text-secondary-foreground"
                          onClick={handleEditProfileNameStart}
                        >
                          <p className="text-sm m-0 text-sm font-semibold text-foreground leading-snug">{selectedProfileRow?.name}</p>
                          <Icon name="edit" size={14} className="text-muted-foreground opacity-0 group-hover/name:opacity-100 transition-opacity" />
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <div className="inline-flex items-center cursor-pointer">
                        <Switch
                          id="perf-enabled"
                          checked={selectedProfileRow?.enabled || false}
                          onCheckedChange={handleEnabledToggle}
                        />
                        <Label className="cursor-pointer select-none text-sm ml-2">Enabled</Label>
                      </div>
                      {canManage && (
                        <>
                          <Button type="button" variant="outline" size="icon" className="border-0" onClick={handleDeleteProfile}>
                            <DeleteIcon sx={{ fontSize: 20 }} className="text-destructive" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Description</h4>
                    {perfDescriptionEditing ? (
                      <div className="flex flex-col gap-2 mt-2">
                        <div className="flex items-center gap-2">
                          <div className="w-full space-y-2">
                            <textarea
                              id="profile-description-edit"
                              name="description"
                              value={perfDescriptionData}
                              onChange={(e) => {
                                setPerfDescriptionData(e.target.value);
                                setPerfDescriptionError("");
                              }}
                              maxLength={255}
                              title="Maximum length: 255 characters"
                              className="w-full flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base text-foreground shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                              autoFocus
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <X onClick={handleCancelProfileDescriptionEdit} className="h-4 w-4 cursor-pointer text-destructive" />
                            <Check
                              onClick={perfDescriptionData !== (selectedProfileRow?.description || "") ? handleSaveProfileDescription : undefined}
                              className={`h-4 w-4 ${perfDescriptionData !== (selectedProfileRow?.description || "") ? "cursor-pointer text-green-500" : "text-muted-foreground opacity-50"}`}
                            />
                          </div>
                        </div>
                        {perfDescriptionError && <p className="text-sm mt-1" style={{ color: "var(--destructive)" }}>{perfDescriptionError}</p>}
                      </div>
                    ) : (
                      <div
                        className="group/description relative inline-flex items-center gap-1.5 rounded-md px-2 py-1 -mx-2 -my-1 cursor-pointer transition-colors hover:bg-secondary hover:text-secondary-foreground"
                        onClick={handleEditProfileDescriptionStart}
                      >
                        <p className="text-sm break-all m-0 text-sm font-semibold text-foreground leading-snug">{selectedProfileRow?.description || ""}</p>
                        <Icon name="edit" size={14} className="text-muted-foreground opacity-0 group-hover/description:opacity-100 transition-opacity" />
                      </div>
                    )}
                  </div>
                  <div data-orientation="horizontal" role="none" className="shrink-0 bg-border h-[1px] w-full !mt-6"></div>
                 

                  {/* Actions Section */}
                  <div className="w-full">
                    <div className="flex items-center justify-between mb-0">
                      <h4 className="text-sm font-semibold text-foreground">Actions</h4>
                      {canManage && (
                        <Button type="button" className="bg-primary text-primary-foreground hover:bg-primary/90" variant="outline" onClick={() => handleAddAction()}>
                          <Icon name="add" size={18} />
                          Add Action
                        </Button>
                      )}
                    </div>

                    <div className="flex flex-col mt-[-10px]">
                      <span className="text-sm text-muted-foreground mt-4 mb-1">
                        What actions should this profile take when triggered?
                      </span>
                      {openConfirmDialog === DIALOGOPENFOR.DELETETHRESHOLDCROSSINGSETTINGACTION && (
                        <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={deleteThresholdCrossingSettingActionClickAction}>
                          Are you sure you want to delete the action <strong>{selectedThresholdCrossingSettingAction.actionType || ""}</strong>?
                        </ConfirmDialog>
                      )}

                      <CustomDataGrid
                        tableId="thresholdProfileAction"
                        rows={rowsActionData}
                        columns={thresholdCrossingSettingActionColumns({ rowCount: rowsActionData.length, canManage: canManage })}
                        checkboxSelection={false}
                        rowCount={rowsActionData.length}
                        loading={false}
                        disableColumnMenu={true}
                        disableRowSelectionOnClick={true}
                        onRowSelectionModelChange={handleOnActionRowSelected}
                        rowSelectionModel={selectedActionRowIDs}
                        getRowId={(row: IThresholdCrossingSettingAction) => row.id}
                        isRowSelectable={() => false}
                        hideFooter={true}
                        sx={{ height: "110px", mt: 1 }}
                        fillContainer
                      />
                    </div>
                  </div>
                  <div data-orientation="horizontal" role="none" className="shrink-0 bg-border h-[1px] w-full !mt-6"></div>
                  {/* Schedule Section */}
                  <div className="w-full">
                    <div className="flex items-center justify-between mb-0">
                      <h2 className="text-sm font-semibold text-foreground">Schedule</h2>
                      {canManage && (
                        <Button type="button" className="bg-primary text-primary-foreground hover:bg-primary/90" variant="outline" onClick={() => handleAddSchedule()}>
                          <Icon name="add" size={18} />
                          Add Schedule
                        </Button>
                      )}
                    </div>

                    <div className="flex flex-col mt-[-10px]">
                      <span className="text-sm text-muted-foreground mt-6 mb-1">
                        Set when alerts should be active for this profile.(Create up to 3)
                      </span>

                    <div>
                      <Tabs value={activeTab} onValueChange={handleScheduleChange} className="w-full flex object-cover flex-col items-start gap-3">
                        <div className="flex items-center justify-between w-full">
                          <TabsList className="inline-flex h-9 w-fit shrink-0 self-start items-stretch gap-0.5 rounded-full border border-border bg-muted p-0.5 text-muted-foreground">
                            {rowsScheduleData.map((record, index) => (
                              <TabsTrigger
                                key={record.id}
                                value={String(index)}
                                className="rounded-full !bg-transparent px-4 h-full inline-flex items-center justify-center text-sm font-medium text-muted-foreground !shadow-none transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 data-[state=active]:!bg-card data-[state=active]:text-foreground data-[state=active]:!shadow-sm"
                              >
                                {index + 1}
                              </TabsTrigger>
                            ))}
                          </TabsList>
                        </div>

                        {/* Tabs Content */}
                        <div className="w-full">
                        {rowsScheduleData.map((record, index) => (
                          <TabsContent
                            key={record.id}
                            value={String(index)}
                            className="m-0" style={{ width: "99%" }}
                          >
                            <div className="rounded-lg border border-border p-3 shadow-sm flex items-center justify-between bg-card" style={{
                              backgroundColor: "var(--left-Navigation-background)"
                            }}>
                              <div className="flex items-center gap-4 text-foreground">
                                {/* Calendar + Days */}
                                <div className="flex items-center gap-2 text-sm">
                                  <CalendarTodayIcon className="text-muted-foreground" sx={{ fontSize: 16 }} />
                                  <div>
                                    <span className="text-sm font-semibold text-foreground leading-snug">{classifyDays(record.days)}</span>{" "}
                                    <span className="font-medium text-muted-foreground pl-1">{record.days.map((d: string) => d.charAt(0) + d.slice(1).toLowerCase()).join(", ")}</span>
                                  </div>
                                </div>
                                {/* Clock + Time */}
                                <div className="flex items-center gap-2 text-sm">
                                  <span className="material-icons select-none text-muted-foreground" aria-hidden="true" style={{ fontSize: 16 }}>schedule</span>
                                  <span className="text-sm font-semibold text-foreground leading-snug">{classifyTimes(record)}</span>
                                </div>

                              </div>

                              {/* Edit / Delete Buttons */}
                              {canManage && (
                                <div className="flex items-center gap-0 text-muted-foreground">
                                  <Button type="button" variant="ghost" size="icon" onClick={() => handleEditSchedule(record)} className="bg-transparent shadow-none h-7 w-7 hover:bg-transparent">
                                    <ModeEditOutlineIcon sx={{ fontSize: 14 }} className="m-0 material-icons select-none text-muted-foreground" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="bg-transparent shadow-none h-7 w-7 hover:bg-transparent"
                                    onClick={() => handleDeleteSchedule(record)}
                                    disabled={rowsScheduleData.length <= 1}
                                  >
                                    <DeleteIcon sx={{ fontSize: 14 }} className={rowsScheduleData.length <= 1 ? "text-muted-foreground opacity-50" : "material-icons select-none text-muted-foreground"} />
                                  </Button>
                                </div>
                              )}
                            </div >
                          </TabsContent >
                        ))}
                        </div>
                      </Tabs >
                    </div >
                  </div>
                  </div>

                  {openConfirmDialog === DIALOGOPENFOR.DELETETHRESHOLDCROSSINGSETTINGSCHEDULE && (
                    <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={handleDeleteScheduleConfirm}>
                      Are you sure you want to delete the schedule <strong>{selectedThresholdCrossingSettingSchedule?.days?.map((d: string) => d.charAt(0) + d.slice(1).toLowerCase()).join(", ") || ""}</strong>?
                    </ConfirmDialog>
                  )}

                  <div data-orientation="horizontal" role="none" className="shrink-0 bg-border h-[1px] w-full !mt-6"></div>
                  {/* Rules Section */}
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-foreground"> Alert when...</h2>
                    {canManage && (
                      <Button type="button" className="bg-primary text-primary-foreground hover:bg-primary/90" variant="outline" onClick={() => handleAddRule()}>
                        <Icon name="add" size={18} />
                        Add Rule
                      </Button>
                    )}
                  </div>
                  {
                    openConfirmDialog === DIALOGOPENFOR.DELETETHRESHOLDCROSSINGSETTINGRULE && (
                      <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={deleteThresholdCrossingSettingRuleClickAction}>
                        Are you sure you want to delete the rule <strong>{selectedThresholdCrossingSettingRule.kpi || ""}</strong>?
                      </ConfirmDialog>
                    )
                  }

                  <div className="w-full" style={{marginTop: "10px" }}>
                    <CustomDataGrid
                      tableId="thresholdProfileRule"
                      rows={rowsRuleData}
                      columns={thresholdCrossingSettingRuleColumns({ rowCount: rowsRuleData.length, canManage: canManage })}
                      checkboxSelection={false}
                      rowCount={rowsRuleData.length}
                      loading={false}
                      disableColumnMenu={true}
                      disableRowSelectionOnClick={true}
                      onRowSelectionModelChange={handleOnActionRowSelected}
                      rowSelectionModel={selectedRuleRowIDs}
                      getRowId={(row: IThresholdCrossingSettingRule) => row.id}
                      isRowSelectable={() => false}
                      hideFooter={true}
                      sx={{ minHeight: "100px" }}
                      fillContainer
                    />
                  </div>
                </div >
              </form >
            </div >
          </div >
        ) : (
          <p className="text-sm text-muted-foreground">No profiles found.</p>
        )}
      </div >
    </>
  );
};

export default ThresholdCrossingSettings;
